/**
 * PALM TERUG OP VOORRAAD
 *
 * Hoort bij snippets/palm-back-in-stock.liquid. Stuurt een e-mailadres naar Klaviyo zodra iemand
 * bericht wil over een uitverkochte maat, en schrijft hem alleen in op de nieuwsbrief als hij het
 * vinkje daarvoor zelf heeft aangezet.
 *
 * Twee endpoints, in deze volgorde:
 *
 *   1. /client/back-in-stock-subscriptions/  de huidige client-API. Antwoordt met 202.
 *   2. /onsite/components/back-in-stock/subscribe  het oude endpoint van de onsite-app. Blijft
 *      staan als terugval, want de client-API valideert het varianten-id tegen de gesynchro-
 *      niseerde Shopify-catalogus en geeft 404 variant_not_found als daar iets niet klopt. Het
 *      oude endpoint is soepeler. Liever een adres binnen via de achterdeur dan helemaal niets.
 *
 * Beide zijn publieke endpoints. Dat is bij Klaviyo niet anders te doen, de pop-up van de
 * app-embed gebruikt precies dezelfde route. Wat hier wel gebeurt is dit: een lokvak tegen
 * botten, en een rem van vijf aanmeldingen per uur per browser. Dat houdt geen vastberaden
 * aanvaller tegen, maar wel het scriptje dat een formulier vindt en er duizend adressen doorheen
 * duwt. Voor de rest leunen we op de bescherming van Klaviyo zelf.
 */

const REVISION = '2024-10-15';
const STORAGE_KEY = 'palm:bis';
const WINDOW_MS = 60 * 60 * 1000;
const MAX_PER_WINDOW = 5;

/**
 * Onthouden waar iemand zich al voor heeft aangemeld, en hoe vaak. Staat in localStorage, dus
 * privacygevoelig is het niet: er staat alleen in welke varianten deze browser heeft aangevraagd.
 * In de privemodus van Safari kan localStorage gooien, vandaar overal een vangnet.
 */
function readStore() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;

    return {
      variants: Array.isArray(parsed?.variants) ? parsed.variants : [],
      stamps: Array.isArray(parsed?.stamps) ? parsed.stamps : [],
    };
  } catch {
    return { variants: [], stamps: [] };
  }
}

function writeStore(store) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Geen opslag beschikbaar. De rem valt weg, het formulier blijft werken.
  }
}

class PalmBackInStock extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.status = this.querySelector('.palm-bis__status');
    this.emailField = this.querySelector('input[type="email"]');
    this.consentField = this.querySelector('input[name="palm_marketing_consent"]');
    this.trapField = this.querySelector('input[name="website"]');
    this.submitButton = this.querySelector('button[type="submit"]');

    if (!this.form || !this.emailField) {
      return;
    }

    this.form.addEventListener('submit', this.#onSubmit);

    // Heeft deze browser zich al voor deze maat aangemeld, dan meteen de bevestiging tonen. Dat
    // scheelt een dubbele aanmelding en het is eerlijker dan doen alsof er niets gebeurd is.
    if (readStore().variants.includes(this.variantId)) {
      this.#showStatus(this.dataset.messageSuccess, 'success');
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  get variantId() {
    return this.dataset.variantId;
  }

  #onSubmit = async (event) => {
    event.preventDefault();

    // Het lokvak is ingevuld, dus dit is geen mens. Bevestigen en verder niets doen.
    if (this.trapField?.value) {
      this.#showStatus(this.dataset.messageSuccess, 'success');
      return;
    }

    const email = this.emailField.value.trim();

    if (!this.emailField.checkValidity() || !email) {
      this.#showStatus(this.dataset.messageInvalid, 'error');
      this.emailField.focus();
      return;
    }

    const store = readStore();
    const now = Date.now();
    store.stamps = store.stamps.filter((stamp) => now - stamp < WINDOW_MS);

    if (store.variants.includes(this.variantId)) {
      this.#showStatus(this.dataset.messageSuccess, 'success');
      return;
    }

    if (store.stamps.length >= MAX_PER_WINDOW) {
      this.#showStatus(this.dataset.messageTooMany, 'error');
      return;
    }

    this.#setBusy(true);

    // De poging telt, niet het resultaat. Anders mag iemand die het tien keer verkeerd doet
    // eindeloos door en houdt de rem alleen de mensen tegen bij wie het meteen goed ging.
    store.stamps.push(now);
    writeStore(store);

    const subscribed = await this.#notifyMe(email);

    if (subscribed) {
      // De nieuwsbrief is een aparte wens en mag de bevestiging niet ophouden of onderuithalen.
      // Lukt hij niet, dan heeft de bezoeker nog steeds waar hij voor kwam.
      if (this.consentField?.checked) {
        this.#subscribeToNewsletter(email).catch(() => {});
      }

      store.variants.push(this.variantId);
      writeStore(store);

      this.#showStatus(this.dataset.messageSuccess, 'success');
    } else {
      this.#showStatus(this.dataset.messageError, 'error');
    }

    this.#setBusy(false);
  };

  async #notifyMe(email) {
    const body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${this.variantId}`,
            },
          },
        },
      },
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.dataset.companyId}`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', revision: REVISION },
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        return true;
      }

      // 400 betekent bij dit endpoint altijd het adres zelf. Dan heeft terugvallen geen zin.
      if (response.status === 400) {
        return false;
      }
    } catch {
      // Netwerk eruit of geblokkeerd door een tracker-blocker. Onder naar de terugval.
    }

    return this.#notifyMeLegacy(email);
  }

  async #notifyMeLegacy(email) {
    const params = new URLSearchParams({
      a: this.dataset.companyId,
      email,
      variant: this.variantId,
      product: this.dataset.productId,
      platform: 'shopify',
    });

    try {
      const response = await fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const result = await response.json();

      return response.ok && result?.success === true;
    } catch {
      return false;
    }
  }

  async #subscribeToNewsletter(email) {
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'PALM terug op voorraad',
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          list: {
            data: { type: 'list', id: this.dataset.listId },
          },
        },
      },
    };

    await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${this.dataset.companyId}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify(body),
    });
  }

  #setBusy(busy) {
    if (!this.submitButton) {
      return;
    }

    this.submitButton.disabled = busy;
    this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  #showStatus(message, kind) {
    if (!this.status || !message) {
      return;
    }

    this.status.textContent = message;
    this.status.className = `palm-bis__status banner banner--${kind}`;
    this.status.hidden = false;

    // Gelukt is gelukt. Het formulier weghalen voorkomt dat iemand er nog eens op drukt en zich
    // afvraagt of het de eerste keer wel goed ging.
    if (kind === 'success') {
      this.form.hidden = true;
      this.querySelector('.palm-bis__intro')?.setAttribute('hidden', '');
      this.querySelector('.palm-bis__privacy')?.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('palm-back-in-stock')) {
  customElements.define('palm-back-in-stock', PalmBackInStock);
}
